/**
 * @file components/CardRegular/component.js
 * @copyright 2016 CBC/Radio-Canada. All rights reserved.
 * @author Fitore Jaha [fitore.jaha@cbc.ca]
 */
import { useEffect, useRef } from "react";
import { string, number, bool, object, func } from "prop-types";
import Headline from "../Headline/component";
import CardImageWrap from "../CardImageWrap/component";
import { Label } from "../../../Label/component";
import LinkWrap from "../../../LinkWrap/component";
import Metadata from "../Metadata/component";
import classNames from "classnames";
import { createLinkProps } from "../../../../utils/dataTransform/utils";
import { useNormalizeClass } from "../../../../hooks/useNormalizeClass";
import { useImageDerivatives } from "../../../../hooks/useImageDerivatives";
import { usePreviewBase } from "../../../../hooks/usePreviewBase";
import { DEFAULT_IMAGE_SIZES } from "./constants";
import { convertImageDerivativesIntoSrcSet } from "../../../../utils/dataTransform/utils";

/**
 * Function representing the CardRegular component.
 *
 * This card is shown in Featured areas of the site, on Landing pages and Story pages (e.g. &#34;More from CBC&#34;)
 *
 * @param {Object} props The component props
 * @returns {Function} Returns the CardRegular component
 */
export const CardRegular = ({
    external,
    title,
    url,
    imageURL,
    image,
    author = {
        name: "",
        image: "",
    },
    className,
    displayComments,
    focus,
    flag,
    flagId,
    baseSection,
    subSection,
    updateTime,
    departments,
    commentCount,
    headlineId,
    metadataId,
    sourceId,
    cardClickHandler,
    videoCardOverlayType,
    videoDuration,
    videoAirDate,
    itemType,
    path,
    showTimestamp = true,
    hideAuthorInfo,
    videoStreamType,
}) => {
    const domNode = useRef(null);

    useEffect(() => {
        // assign focus based on props
        if (focus) {
            domNode.current.focus();
        }
    }, [focus]);

    const flagClassName = useNormalizeClass("flag", flag);
    const classes = classNames("card", flagClassName, "cardRegular", className);

    const { authorName, authorImage } = {
        authorName: author.name,
        authorImage: author.image,
    };

    const metaProps = {
        baseSection: baseSection,
        subSection: subSection,
        authorName: authorName,
        authorImage: authorImage,
        updated: updateTime,
        showTimestamp: showTimestamp,
        departments: departments,
        url: url,
        displayComments: displayComments,
        commentCount: commentCount,
        path: path,
        hideAuthorInfo,
    };

    const imageUrl = usePreviewBase(imageURL);

    const [srcSet] = useImageDerivatives(imageUrl);

    let imageSrcSet = srcSet;
    if (imageSrcSet.length === 0) {
        imageSrcSet = convertImageDerivativesIntoSrcSet(image);
        imageSrcSet =
            imageSrcSet?.filter((item) => !item.includes("220w")) || [];
    }

    const imageProps = {
        url: imageUrl,
        srcSet: imageSrcSet,
        sizes: DEFAULT_IMAGE_SIZES,
    };

    const videoLinkImageOverlayProps = {
        videoCardOverlayType,
        videoDuration,
        videoAirDate,
        videoStreamType,
    };

    const labelProps = {
        flag: flag,
        external: external,
        id: flagId,
    };

    return (
        <LinkWrap
            className={classes}
            {...createLinkProps({
                sourceId,
                external,
                url,
                cardClickHandler,
            })}
            data-cy={`type-${itemType}`}
            data-testid="card-regular"
            ref={domNode}
        >
            <CardImageWrap {...{ imageProps, videoLinkImageOverlayProps }} />
            <div className="contentWrapper">
                <div className="card-content">
                    <div className="card-content-top">
                        <Label {...labelProps} />
                        <Headline id={headlineId} text={title} />
                    </div>
                    <div className="card-content-bottom">
                        <Metadata id={metadataId} {...metaProps} />
                    </div>
                </div>
            </div>
        </LinkWrap>
    );
};

CardRegular.propTypes = {
    // REQUIRED

    // Is the URL external (in which case we don't handle it)?
    external: bool.isRequired,
    // The headline to display.
    title: string.isRequired,
    // The URL that the card links to
    url: string.isRequired,
    // The image URL of the image
    imageURL: string.isRequired,

    // OPTIONAL
    // Author name and image object
    author: object,
    className: string,
    // Display comments for this card
    displayComments: bool,

    focus: bool,
    flag: string,
    flagId: string,
    baseSection: string,
    subSection: string,
    updateTime: number,
    departments: object,
    commentCount: number,
    onClick: func,
    headlineId: string,
    metadataId: string,
    sourceId: string,
    cardClickHandler: func,
    itemType: string,
    showTimestamp: bool,
};

export default CardRegular;
